/**
 * OperatorIdentity Component
 * ===========================
 * Identity section within the Intelligence tab.
 * 
 * This is NOT a personality system - it's structured configuration that influences:
 * - Communication style (tone, verbosity)
 * - Execution posture (risk level)
 * - Confirmation behavior
 * - Tool preference bias
 * 
 * Key Principles:
 * - NEVER self-modifying
 * - NEVER auto-evolving
 * - Only human-editable
 * - Fully versioned and auditable
 * - Safety constraints cannot be overridden
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';

import {
  ArrowCounterClockwise,
  CaretDown,
  CaretRight,
  ChatText,
  Check,
  ClockCounterClockwise,
  Eye,
  FloppyDisk,
  GearFine,
  Lightning,
  Lock,
  PencilSimple,
  Shield,
  User,
  Warning,
  X
} from '@phosphor-icons/react';
// Schema options
const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', description: 'Formal, business-appropriate communication' },
  { value: 'direct', label: 'Direct', description: 'Straight to the point, minimal preamble' },
  { value: 'analytical', label: 'Analytical', description: 'Data-driven, detailed explanations' },
  { value: 'minimal', label: 'Minimal', description: 'Brief responses, essential info only' },
];

const VERBOSITY_OPTIONS = [
  { value: 'low', label: 'Low', description: 'Concise, brief responses' },
  { value: 'medium', label: 'Medium', description: 'Balanced detail level' },
  { value: 'high', label: 'High', description: 'Comprehensive explanations' },
];

const RISK_OPTIONS = [
  { value: 'conservative', label: 'Conservative', description: 'Extra caution, more confirmations' },
  { value: 'balanced', label: 'Balanced', description: 'Standard caution level' },
  { value: 'assertive', label: 'Assertive', description: 'Faster execution, fewer pauses' },
];

const OperatorIdentity = ({ operatorId, onUpdate }) => {
  const { activeOrganization } = useOrganization();
  
  // State
  const [loading, setLoading] = useState(true);
  const [identity, setIdentity] = useState(null);
  const [history, setHistory] = useState([]);
  const [orgSettings, setOrgSettings] = useState(null);
  
  // PencilSimple state
  const [isEditing, setIsEditing] = useState(false);
  const [editProfile, setEditProfile] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [saving, setSaving] = useState(false);
  
  // ClockCounterClockwise view state
  const [showHistory, setShowHistory] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(null);
  const [rollbackReason, setRollbackReason] = useState('');
  
  // Fetch identity data
  const fetchData = useCallback(async () => {
    if (!activeOrganization || !operatorId) return;
    
    setLoading(true);
    try {
      const orgId = activeOrganization.organization_id;
      
      // Fetch identity
      const identityRes = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/identity/${operatorId}`,
        {}, orgId
      );
      if (identityRes.ok) {
        const data = await identityRes.json();
        setIdentity(data);
      }
      
      // Fetch history
      const historyRes = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/identity/${operatorId}/history`,
        {}, orgId
      );
      if (historyRes.ok) {
        const data = await historyRes.json();
        setHistory(data.history || []);
      }
      
      // Fetch org settings
      const settingsRes = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/identity/org/settings`,
        {}, orgId
      );
      if (settingsRes.ok) {
        setOrgSettings(await settingsRes.json());
      }
      
    } catch (error) {
      console.error('Error fetching identity data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeOrganization, operatorId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Start editing
  const handleStartEdit = () => {
    if (identity?.profile) {
      setEditProfile(JSON.parse(JSON.stringify(identity.profile)));
      setChangeReason('');
      setIsEditing(true);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditProfile(null);
    setChangeReason('');
  };

  // FloppyDisk changes
  const handleSave = async () => {
    if (!activeOrganization || !editProfile) return;
    
    setSaving(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/identity/${operatorId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            profile: editProfile,
            change_reason: changeReason || 'Identity profile updated'
          })
        },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Identity updated to ${data.version_label}`);
        setIsEditing(false);
        setEditProfile(null);
        setChangeReason('');
        fetchData();
        onUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update identity');
      }
    } catch (error) {
      toast.error('Failed to save identity');
    } finally {
      setSaving(false);
    }
  };

  // Rollback
  const handleRollback = async (targetVersion) => {
    if (!activeOrganization) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/identity/${operatorId}/rollback`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            target_version: targetVersion,
            reason: rollbackReason || `Rollback to v${targetVersion}`
          })
        },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Rolled back to v${targetVersion} (now ${data.version_label})`);
        setShowRollbackModal(null);
        setRollbackReason('');
        fetchData();
        onUpdate?.();
      } else {
        toast.error('Rollback failed');
      }
    } catch (error) {
      toast.error('Rollback failed');
    }
  };

  // Update profile field
  const updateProfile = (path, value) => {
    if (!editProfile) return;
    
    const parts = path.split('.');
    const newProfile = JSON.parse(JSON.stringify(editProfile));
    let current = newProfile;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
    
    setEditProfile(newProfile);
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-[rgba(255,255,255,0.05)] rounded w-1/3"></div>
        <div className="h-32 bg-[rgba(255,255,255,0.05)] rounded"></div>
      </div>
    );
  }

  const profile = identity?.profile || {};
  const currentIdentity = identity?.identity || {};

  return (
    <div className="space-y-6" data-testid="operator-identity">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
            <User size={20} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-white">Agent Identity</h3>
            <p className="text-sm text-gray-400">
              {currentIdentity.version_label || 'Not configured'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)] rounded-lg transition-all"
            data-testid="identity-history-btn"
          >
            <ClockCounterClockwise size={16} />
            ClockCounterClockwise
          </button>
          
          {!isEditing ? (
            <button
              onClick={handleStartEdit}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 rounded-lg transition-all"
              data-testid="identity-edit-btn"
            >
              <PencilSimple size={16} />
              PencilSimple Identity
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancelEdit}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)] rounded-lg transition-all"
              >
                <X size={16} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-all disabled:opacity-50"
                data-testid="identity-save-btn"
              >
                <FloppyDisk size={16} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* PencilSimple Mode Change Reason */}
      {isEditing && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="glass rounded-xl p-4 border border-purple-500/30"
        >
          <label className="block text-sm text-gray-400 mb-2">Change Reason (optional)</label>
          <input
            type="text"
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            placeholder="Describe why you're making this change..."
            className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
          />
        </motion.div>
      )}

      {/* Profile Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Communication Style */}
        <ProfileSection
          title="Communication Style"
          icon={ChatText}
          color="blue"
        >
          <ProfileField
            label="Tone"
            value={profile.communication_style?.tone}
            options={TONE_OPTIONS}
            isEditing={isEditing}
            onChange={(v) => updateProfile('communication_style.tone', v)}
          />
          <ProfileField
            label="Verbosity"
            value={profile.communication_style?.verbosity}
            options={VERBOSITY_OPTIONS}
            isEditing={isEditing}
            onChange={(v) => updateProfile('communication_style.verbosity', v)}
          />
          <ProfileToggle
            label="Use Filler Language"
            value={profile.communication_style?.use_filler_language}
            isEditing={isEditing}
            onChange={(v) => updateProfile('communication_style.use_filler_language', v)}
          />
          <ProfileToggle
            label="Confirm Before External Actions"
            value={profile.communication_style?.confirm_before_external_actions}
            isEditing={isEditing}
            onChange={(v) => updateProfile('communication_style.confirm_before_external_actions', v)}
          />
        </ProfileSection>

        {/* Execution Posture */}
        <ProfileSection
          title="Execution Posture"
          icon={Lightning}
          color="yellow"
        >
          <ProfileField
            label="Risk Level"
            value={profile.execution_posture?.risk_level}
            options={RISK_OPTIONS}
            isEditing={isEditing}
            onChange={(v) => updateProfile('execution_posture.risk_level', v)}
          />
          <ProfileToggle
            label="Confirm Destructive Actions"
            value={true}
            isEditing={false}
            locked={true}
            lockReason="Safety constraint - cannot be disabled"
          />
          <ProfileToggle
            label="Prefer API Over Browser"
            value={profile.execution_posture?.prefer_structured_api_over_browser}
            isEditing={isEditing}
            onChange={(v) => updateProfile('execution_posture.prefer_structured_api_over_browser', v)}
          />
          <ProfileToggle
            label="Avoid Browser for Local State"
            value={profile.execution_posture?.avoid_browser_for_local_state}
            isEditing={isEditing}
            onChange={(v) => updateProfile('execution_posture.avoid_browser_for_local_state', v)}
          />
        </ProfileSection>

        {/* Tool Preferences */}
        <ProfileSection
          title="Tool Preferences"
          icon={Settings}
          color="purple"
        >
          <ProfileToggle
            label="Prefer Native Integrations"
            value={profile.tool_preference_bias?.prefer_native_integrations}
            isEditing={isEditing}
            onChange={(v) => updateProfile('tool_preference_bias.prefer_native_integrations', v)}
          />
          <ProfileToggle
            label="Prefer MCP Over Scraping"
            value={profile.tool_preference_bias?.prefer_mcp_over_scraping}
            isEditing={isEditing}
            onChange={(v) => updateProfile('tool_preference_bias.prefer_mcp_over_scraping', v)}
          />
        </ProfileSection>

        {/* Intelligence Behavior */}
        <ProfileSection
          title="Intelligence Behavior"
          icon={Shield}
          color="green"
        >
          <ProfileToggle
            label="Propose Improvements"
            value={profile.intelligence_behavior?.propose_improvements}
            isEditing={isEditing}
            onChange={(v) => updateProfile('intelligence_behavior.propose_improvements', v)}
          />
          <ProfileToggle
            label="Auto-Apply Changes"
            value={false}
            isEditing={false}
            locked={true}
            lockReason="Safety constraint - changes require approval"
          />
        </ProfileSection>
      </div>

      {/* Core Role Description */}
      <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
        <div className="flex items-center gap-2 mb-3">
          <User size={16} className="text-gray-400" />
          <span className="text-sm font-medium text-white">Core Role</span>
        </div>
        {isEditing ? (
          <textarea
            value={editProfile?.core_role?.description || ''}
            onChange={(e) => updateProfile('core_role.description', e.target.value)}
            rows={2}
            className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
            placeholder="Describe the agent's core role..."
          />
        ) : (
          <p className="text-gray-300 text-sm">
            {profile.core_role?.description || 'An AI operator that executes tasks reliably and transparently.'}
          </p>
        )}
      </div>

      {/* Org Identity Sharing */}
      {orgSettings && (
        <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GearFine size={16} className="text-gray-400" />
              <span className="text-sm text-gray-400">Org Identity Sharing:</span>
            </div>
            <span className={`text-sm ${orgSettings.share_identity_enabled ? 'text-green-400' : 'text-gray-500'}`}>
              {orgSettings.share_identity_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      )}

      {/* Version ClockCounterClockwise Panel */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="glass rounded-xl border border-[rgba(255,255,255,0.1)] overflow-hidden"
          >
            <div className="p-4 border-b border-[rgba(255,255,255,0.1)]">
              <h4 className="text-sm font-medium text-white flex items-center gap-2">
                <ClockCounterClockwise size={16} />
                Version ClockCounterClockwise
              </h4>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {history.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No version history yet
                </div>
              ) : (
                history.map((version, idx) => (
                  <div
                    key={version.id}
                    className={`p-4 border-b border-[rgba(255,255,255,0.05)] last:border-0 ${
                      version.is_active ? 'bg-purple-500/5' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${version.is_active ? 'bg-green-400' : 'bg-gray-500'}`} />
                        <div>
                          <div className="text-sm font-medium text-white">
                            {version.version_label}
                            {version.is_active && (
                              <span className="ml-2 text-xs text-green-400">(Active)</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(version.created_at).toLocaleString()}
                            {version.change_reason && ` • ${version.change_reason}`}
                          </div>
                        </div>
                      </div>
                      
                      {!version.is_active && (
                        <button
                          onClick={() => setShowRollbackModal(version.version_number)}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)] rounded transition-all"
                        >
                          <ArrowCounterClockwise size={12} />
                          Rollback
                        </button>
                      )}
                    </div>
                    
                    {/* Diff display */}
                    {version.diff_from_previous && Object.keys(version.diff_from_previous).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[rgba(255,255,255,0.05)]">
                        <div className="text-xs text-gray-500 mb-1">Changes:</div>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(version.diff_from_previous).slice(0, 3).map(([key, change]) => (
                            <span key={key} className="text-xs px-2 py-0.5 bg-[rgba(255,255,255,0.05)] rounded text-gray-400">
                              {key.split('.').pop()}
                            </span>
                          ))}
                          {Object.keys(version.diff_from_previous).length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{Object.keys(version.diff_from_previous).length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rollback Modal */}
      <AnimatePresence>
        {showRollbackModal !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRollbackModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="glass rounded-xl p-6 max-w-md w-full border border-[rgba(255,255,255,0.1)]"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <ArrowCounterClockwise size={20} className="text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Rollback Identity</h3>
                  <p className="text-sm text-gray-400">Restore version {showRollbackModal}</p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Reason (optional)</label>
                <input
                  type="text"
                  value={rollbackReason}
                  onChange={(e) => setRollbackReason(e.target.value)}
                  placeholder="Why are you rolling back?"
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRollbackModal(null)}
                  className="flex-1 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)] rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRollback(showRollbackModal)}
                  className="flex-1 px-4 py-2 text-sm bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded-lg transition-all"
                >
                  Rollback
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Profile Section Component
const ProfileSection = ({ title, icon: Icon, color, children }) => {
  const colorClasses = {
    blue: 'from-blue-500/20 to-cyan-500/20 text-blue-400',
    yellow: 'from-yellow-500/20 to-orange-500/20 text-yellow-400',
    purple: 'from-purple-500/20 to-pink-500/20 text-purple-400',
    green: 'from-green-500/20 to-emerald-500/20 text-green-400',
  };
  
  return (
    <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
          <Icon size={16} />
        </div>
        <span className="text-sm font-medium text-white">{title}</span>
      </div>
      <div className="space-y-3">
        {children}
      </div>
    </div>
  );
};

// Profile Field Component (Dropdown)
const ProfileField = ({ label, value, options, isEditing, onChange }) => {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-400">{label}</span>
      {isEditing ? (
        <select
          value={value || options[0]?.value}
          onChange={(e) => onChange(e.target.value)}
          className="px-3 py-1 text-sm bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <span className="text-sm text-white capitalize">
          {options.find(o => o.value === value)?.label || value}
        </span>
      )}
    </div>
  );
};

// Profile Toggle Component
const ProfileToggle = ({ label, value, isEditing, onChange, locked, lockReason }) => {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-400">{label}</span>
        {locked && (
          <div className="relative group">
            <Lock size={12} className="text-gray-500" />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-gray-800 text-gray-300 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              {lockReason}
            </div>
          </div>
        )}
      </div>
      {isEditing && !locked ? (
        <button
          onClick={() => onChange(!value)}
          className={`w-10 h-5 rounded-full transition-all ${
            value ? 'bg-purple-500' : 'bg-gray-600'
          }`}
        >
          <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${
            value ? 'translate-x-5' : 'translate-x-0.5'
          }`} />
        </button>
      ) : (
        <span className={`text-sm ${value ? 'text-green-400' : 'text-gray-500'}`}>
          {value ? 'Yes' : 'No'}
        </span>
      )}
    </div>
  );
};

export default OperatorIdentity;
